import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CreativesResponse,
  GetCreativeTypesResponse,
  GetReusableCreativesResponse,
  PatchUpdateCreativeStatusRequest,
  PatchUpdateCreativeStatusResponse,
  ReuseCreativeRequest,
  SkipCreativeStepRequest,
} from './models';
import { API_EVENT_TAGS } from 'lib.store/src/tags';
import { organizerEvents } from '../events/events.api';
import { GetOrganizerEventByIdResponse } from '../events/models';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';

export const creativesApi = createApi({
  reducerPath: 'creativesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    API_EVENT_TAGS.ORGANIZER_CREATIVES,
    API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID,
    API_EVENT_TAGS.ORGANIZER_EVENTS,
  ],
  endpoints: (builder) => ({
    getCreatives: builder.query<CreativesResponse, { eventId: string | number; page?: number }>({
      query: ({ eventId, page = 1 }) => ({
        url: `/organizer/events/${eventId}/setup/creatives`,
        params: { page },
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      providesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_CREATIVES, id: eventId },
      ],
    }),
    patchCreativeStatusById: builder.mutation<
      PatchUpdateCreativeStatusResponse,
      PatchUpdateCreativeStatusRequest
    >({
      query: ({ eventId, creativeId, status, transition, comment = '' }) => ({
        url: `/organizer/events/${eventId}/setup/creatives/${creativeId}/status/${status}`,
        method: 'PATCH',
        body: { comment, transition },
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            organizerEvents.util.invalidateTags([
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
            ]),
          );
        } catch {
          // If the query fails, we don't need to invalidate anything
        }
      },
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_CREATIVES, id: eventId },
      ],
    }),
    patchCreativeStatusAll: builder.mutation<CreativesResponse, PatchUpdateCreativeStatusRequest>({
      query: ({ eventId, status, transition, comment = '' }) => ({
        url: `/organizer/events/${eventId}/setup/creatives/all/status/${status}`,
        method: 'PATCH',
        body: { comment, transition },
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            organizerEvents.util.invalidateTags([
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
            ]),
          );
        } catch {
          // If the query fails, we don't need to invalidate anything
        }
      },
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_CREATIVES, id: eventId },
      ],
    }),
    getReusableCreatives: builder.query<GetReusableCreativesResponse, { eventId: string | number }>(
      {
        query: ({ eventId }) => ({
          url: `/organizer/events/${eventId}/setup/creatives/reusable`,
          headers: {
            Accept: 'application/json',
          },
        }),
        providesTags: (_result, _error, { eventId }) => [
          { type: API_EVENT_TAGS.ORGANIZER_CREATIVES, id: `reusable-${eventId}` },
        ],
      },
    ),
    getCreativeTypes: builder.query<GetCreativeTypesResponse, { eventId: string | number }>({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}/setup/creatives/types`,
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    reuseCreative: builder.mutation<PatchUpdateCreativeStatusResponse, ReuseCreativeRequest>({
      query: ({ eventId, creativeId }) => ({
        url: `/organizer/events/${eventId}/setup/creatives/reuse/${creativeId}`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      }),
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            organizerEvents.util.invalidateTags([
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
            ]),
          );
        } catch {
          // if the mutation fails there is nothing to invalidate
        }
      },
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_CREATIVES, id: eventId },
        { type: API_EVENT_TAGS.ORGANIZER_CREATIVES, id: `reusable-${eventId}` },
      ],
    }),
    skipCreativeStep: builder.mutation<GetOrganizerEventByIdResponse, SkipCreativeStepRequest>({
      query: ({ eventId, skipped }) => ({
        url: `/organizer/events/${eventId}/setup/creatives/skip`,
        method: 'PATCH',
        body: { skipped },
        headers: {
          Accept: 'application/json',
        },
      }),
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // update the event-detail cache directly from the response (per AGENTS.md) so the
          // checklist and skip checkbox reflect immediately without a redundant refetch
          dispatch(
            organizerEvents.util.updateQueryData(
              'getOrganizerEventById',
              { eventId: String(eventId) },
              () => data,
            ),
          );
          dispatch(
            organizerEvents.util.invalidateTags([{ type: API_EVENT_TAGS.ORGANIZER_EVENTS }]),
          );
        } catch {
          // if the mutation fails there is nothing to update
        }
      },
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_CREATIVES, id: eventId },
      ],
    }),
  }),
});

export const {
  useGetCreativesQuery,
  usePatchCreativeStatusByIdMutation,
  usePatchCreativeStatusAllMutation,
  useGetReusableCreativesQuery,
  useGetCreativeTypesQuery,
  useReuseCreativeMutation,
  useSkipCreativeStepMutation,
} = creativesApi;
